import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Search } from "lucide-react";
import { api, errMsg, formatDate } from "@/lib/api";
import { useCatalog } from "@/lib/useCatalog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CategoryIcon } from "@/components/CategoryIcon";
import { CitySelect } from "@/components/CitySelect";
import { PhotoUploader } from "@/components/PhotoUploader";
import { DatePicker } from "@/components/DatePicker";

const STEPS = ["Pilih jasa", "Detail kebutuhan", "Lokasi & jadwal", "Tinjau & kirim"];

const Stepper = ({ step }) => (
  <ol className="flex items-center gap-2" data-testid="wizard-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className="flex flex-1 items-center gap-2">
        <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold",
          i < step ? "bg-teal-700 text-white" : i === step ? "bg-teal-50 text-teal-800 ring-2 ring-teal-600" : "bg-stone-100 text-stone-400")}>
          {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
        </span>
        <span className={cn("hidden text-xs font-medium md:inline", i === step ? "text-stone-900" : "text-stone-400")}>{s}</span>
        {i < STEPS.length - 1 && <span className="h-px flex-1 bg-stone-200" />}
      </li>
    ))}
  </ol>
);

const PickCategory = ({ groups, value, onPick }) => {
  const [filter, setFilter] = useState("");
  const f = filter.toLowerCase();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-3">
        <Search className="h-4 w-4 text-stone-400" />
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Cari jenis jasa…" data-testid="wizard-category-filter"
          className="h-11 w-full bg-transparent text-sm focus:outline-none" />
      </div>
      {groups.map((g) => {
        const subs = g.subcategories.filter((s) => s.name.toLowerCase().includes(f));
        if (!subs.length) return null;
        return (
          <div key={g.slug}>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-stone-500">{g.name}</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {subs.map((s) => (
                <button key={s.slug} type="button" onClick={() => onPick(s.slug)} data-testid={`wizard-category-${s.slug}`}
                  className={cn("flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-[border-color,background-color] duration-150",
                    value === s.slug ? "border-teal-600 bg-teal-50 text-teal-900" : "border-stone-200 bg-white text-stone-700 hover:border-stone-300")}>
                  <CategoryIcon name={s.icon} className="h-4 w-4 text-teal-700" /> {s.name}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Question = ({ q, value, onChange }) => (
  <div className="space-y-2.5" data-testid={`question-${q.key}`}>
    <Label className="text-sm font-semibold text-stone-900">{q.label} {q.required ? <span className="text-rose-600">*</span> : <span className="font-normal text-stone-400">(opsional)</span>}</Label>
    {q.type === "pilihan" ? (
      <div className="flex flex-wrap gap-2">
        {q.options.map((o, i) => (
          <button key={o} type="button" onClick={() => onChange(o)} data-testid={`question-${q.key}-option-${i}`}
            className={cn("rounded-full border px-4 py-2 text-sm transition-[border-color,background-color] duration-150",
              value === o ? "border-teal-600 bg-teal-700 text-white" : "border-stone-200 bg-white text-stone-700 hover:border-stone-400")}>
            {o}
          </button>
        ))}
      </div>
    ) : (
      <Input type={q.type === "angka" ? "number" : "text"} min={q.type === "angka" ? 0 : undefined} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)} className="h-11 max-w-sm rounded-xl" data-testid={`question-${q.key}-input`} />
    )}
  </div>
);

export default function RequestWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { groups, cities, subcategories } = useCatalog();
  const initial = params.get("kategori") || "";
  const [step, setStep] = useState(initial ? 1 : 0);
  const [slug, setSlug] = useState(initial);
  const [cat, setCat] = useState(null);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ city: "", address: "", preferred_date: "", description: "", photos: [] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get(`/categories/${slug}`).then((r) => setCat(r.data)).catch(() => { setSlug(""); setStep(0); });
  }, [slug]);

  const missingQ = useMemo(() => (cat?.questions || []).find((q) => q.required && (answers[q.key] === undefined || answers[q.key] === "")), [cat, answers]);
  const step2Error = !form.city ? "Pilih kota" : form.address.trim().length < 5 ? "Isi alamat dengan lengkap" : !form.preferred_date ? "Pilih tanggal" : form.description.trim().length < 10 ? "Deskripsi minimal 10 karakter" : null;

  const next = () => {
    if (step === 0 && !slug) return toast.error("Pilih jenis jasa terlebih dahulu");
    if (step === 1 && missingQ) return toast.error(`Mohon jawab: ${missingQ.label}`);
    if (step === 2 && step2Error) return toast.error(step2Error);
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/requests", { category_slug: slug, answers, ...form });
      toast.success("Permintaan terkirim! Penyedia yang sesuai akan segera mengirim penawaran.");
      navigate(`/permintaan/${data.request_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cityName = cities.find((c) => c.slug === form.city)?.name;

  return (
    <div className="container-page max-w-4xl py-10 lg:py-14" data-testid="request-wizard">
      <p className="eyebrow">Minta Penawaran</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">
        {cat && step > 0 ? cat.name : "Jasa apa yang Anda butuhkan?"}
      </h1>
      <div className="mt-8"><Stepper step={step} /></div>
      <div className="card-soft mt-8 p-6 sm:p-8">
        {step === 0 && <PickCategory groups={groups} value={slug} onPick={(s) => { setSlug(s); setAnswers({}); setCat(null); setStep(1); }} />}
        {step === 1 && (
          <div className="space-y-7">
            {!cat ? <Loader2 className="h-5 w-5 animate-spin text-stone-400" /> : cat.questions.map((q) => (
              <Question key={q.key} q={q} value={answers[q.key]} onChange={(v) => setAnswers({ ...answers, [q.key]: v })} />
            ))}
          </div>
        )}
        {step === 2 && (
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Kota <span className="text-rose-600">*</span></Label>
              <CitySelect cities={cities} value={form.city} onChange={(city) => setForm({ ...form, city })} required testId="wizard-city-select" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Tanggal yang diinginkan <span className="text-rose-600">*</span></Label>
              <DatePicker value={form.preferred_date} onChange={(preferred_date) => setForm({ ...form, preferred_date })} testId="wizard-date-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="address">Detail alamat <span className="text-rose-600">*</span></Label>
              <Input id="address" value={form.address} onChange={setF("address")} placeholder="Nama jalan, nomor, kelurahan/kecamatan" className="h-11 rounded-xl" data-testid="wizard-address-input" />
              <p className="text-xs text-stone-500">Alamat lengkap hanya ditampilkan ke penyedia yang Anda pilih.</p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="desc">Deskripsi kebutuhan <span className="text-rose-600">*</span></Label>
              <Textarea id="desc" rows={5} value={form.description} onChange={setF("description")} placeholder="Ceritakan kondisi dan hasil yang Anda harapkan…" className="rounded-xl" data-testid="wizard-description-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Foto (opsional, maks. 5)</Label>
              <PhotoUploader value={form.photos} onChange={(photos) => setForm({ ...form, photos })} max={5} testId="wizard-photos" />
            </div>
          </div>
        )}
        {step === 3 && cat && (
          <div className="space-y-6" data-testid="wizard-review">
            <dl className="grid gap-3 sm:grid-cols-2">
              {[["Jenis jasa", cat.name], ["Kota", cityName], ["Tanggal", formatDate(form.preferred_date)], ["Alamat", form.address],
                ...cat.questions.filter((q) => answers[q.key] !== undefined && answers[q.key] !== "").map((q) => [q.label, String(answers[q.key])])].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-stone-200 px-4 py-3">
                  <dt className="text-xs text-stone-500">{k}</dt><dd className="mt-0.5 text-sm font-semibold text-stone-900">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="whitespace-pre-line rounded-xl bg-stone-50 p-4 text-sm text-stone-700">{form.description}</p>
            <p className="text-sm text-stone-500">{form.photos.length} foto dilampirkan · Maksimal 3 penyedia dapat mengirim penawaran.</p>
          </div>
        )}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="rounded-xl" data-testid="wizard-back-button">
          <ArrowLeft className="mr-2 h-4 w-4" /> Kembali
        </Button>
        {step < 3 ? (
          step > 0 && <Button onClick={next} className="rounded-xl bg-teal-700 px-6 hover:bg-teal-800" data-testid="wizard-next-button">Lanjut <ArrowRight className="ml-2 h-4 w-4" /></Button>
        ) : (
          <Button onClick={submit} disabled={submitting} className="rounded-xl bg-teal-700 px-6 hover:bg-teal-800" data-testid="wizard-submit-button">
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kirim Permintaan
          </Button>
        )}
      </div>
      {subcategories.length === 0 && step === 0 && <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-stone-400" />}
    </div>
  );
}
